import { ApplicationCorrespondenceDirection } from '@/types/models';
import { ExteriorApplicationStatus, isApplicationOpen } from '@/utils/application-status';

/**
 * The stages staff actually work in (#369).
 *
 * These are NOT the status enum. Status records where a request formally is; a stage
 * answers "is this on my desk right now?", which needs the status AND whether anyone has
 * read it AND who sent the last message. Three requests all sitting in `Submitted` can be
 * brand new, already triaged, or waiting on an answer — one column cannot say that.
 *
 * Derived, never stored, for the same reason `applicationWaitingSide` is: a stored copy
 * would drift the moment a message arrived by a path that forgot to update it.
 */
export type ApplicationStage =
	| 'new'
	| 'needs-review'
	| 'waiting-on-owner'
	| 'awaiting-board'
	| 'approved'
	| 'rejected'
	| 'withdrawn';

export const APPLICATION_STAGE_LABEL: Record<ApplicationStage, string> = {
	'new': 'New',
	'needs-review': 'Needs Review',
	'waiting-on-owner': 'Waiting on Owner',
	'awaiting-board': 'Awaiting Board Review',
	approved: 'Approved',
	rejected: 'Rejected',
	withdrawn: 'Withdrawn',
};

/** Rail order — the path a request actually travels. */
export const APPLICATION_STAGES: ApplicationStage[] = [
	'new', 'needs-review', 'waiting-on-owner', 'awaiting-board', 'approved', 'rejected', 'withdrawn',
];

/**
 * The stages where WE owe the next action. What the nav badge counts.
 *
 * Deliberately excludes `awaiting-board` — a request with the board is genuinely not our
 * move, and counting it would leave the badge permanently lit through the weeks a board
 * takes to meet, which is how a badge stops meaning anything.
 */
export const PENDING_US_STAGES: ApplicationStage[] = ['new', 'needs-review'];

/** Open, but the next move is the homeowner's or the board's — the rail's "Pending" group (#581). */
export const PENDING_OTHERS_STAGES: ApplicationStage[] = ['waiting-on-owner', 'awaiting-board'];

export const APPLICATION_STAGE_ICON: Record<ApplicationStage, string> = {
	'new': 'sparkles-outline',
	'needs-review': 'eye-outline',
	'waiting-on-owner': 'hourglass-outline',
	'awaiting-board': 'people-circle-outline',
	approved: 'checkmark-circle-outline',
	rejected: 'close-circle-outline',
	withdrawn: 'remove-circle-outline',
};

export interface StageInput {
	status: number;
	/** Direction of the NEWEST linked message, or null when there are none. */
	lastDirection: number | null;
	/** True when every linked message has been read. */
	allRead: boolean;
}

/**
 * Which stage a request is in.
 *
 * The order of these checks is the rule:
 *
 * 1. Decided requests are terminal. Late correspondence does not reopen one.
 * 2. Reviewed / SubmittedToBoard both read as "with the board" to a staffer — one is
 *    ready to send and one has been sent, and neither is anybody's action here.
 * 3. An OUTBOUND last message means we asked and are waiting. This is checked before
 *    the status so that a request formally parked in "Needs Additional Info" flips back
 *    to us the instant the homeowner answers — the workflow rule that motivated stages.
 * 4. Unread and untouched is NEW; anything else still on us is NEEDS REVIEW.
 */
export function applicationStage({ status, lastDirection, allRead }: StageInput): ApplicationStage {
	if (status === ExteriorApplicationStatus.Approved) return 'approved';
	if (status === ExteriorApplicationStatus.Rejected) return 'rejected';
	if (status === ExteriorApplicationStatus.Withdrawn) return 'withdrawn';
	if (!isApplicationOpen(status)) return 'approved';

	if (
		status === ExteriorApplicationStatus.Reviewed
		|| status === ExteriorApplicationStatus.SubmittedToBoard
	) {
		return 'awaiting-board';
	}

	// We spoke last → the ball is with the homeowner. An inbound reply lands below.
	if (lastDirection === ApplicationCorrespondenceDirection.Outbound) return 'waiting-on-owner';

	// No reply yet on an explicit request for more info.
	if (status === ExteriorApplicationStatus.NeedsAdditionalInfo && lastDirection === null) {
		return 'waiting-on-owner';
	}

	// On us. Brand new (nobody has opened it) versus seen-but-not-actioned.
	return allRead ? 'needs-review' : 'new';
}
